import ApplicationController from './application_controller'

export default class extends ApplicationController {

  // ---------------------------------------------------------------------------
  // Targets
  // ---------------------------------------------------------------------------
  static targets = [
    // Top bar
    "entityBtn",      // the context switcher trigger button
    "contextMenu",    // context dropdown panel
    "contextSearch",  // search input inside context dropdown
    "contextGroups",  // container holding all .gv3-context-group elements

    // Action dropdowns (each lives next to its trigger inside .gv3-header__action-wrap)
    "createMenu",
    "helpMenu",
    "settingsMenu",

    // Nav bar
    "mainMenu",       // <ul> holding nav items
    "overflowBtn",    // the >> button
    "overflowMenu",   // overflow dropdown panel
    "searchBox",      // .gv3-header__search wrapper
  ]

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------
  connect() {
    this._bindNavButtons()
    this._bindContextItems()
    this._bindOutsideClick()
    this._bindEscape()
    this._bindResize()

    // Sidebar + overlay live outside this element — grab by id
    this._sidebar = document.getElementById("gv3Sidebar")
    this._overlay = document.getElementById("gv3SidebarOverlay")

    if (this._overlay) {
      this._overlay.addEventListener("click", () => this.closeSidebar())
    }

    // Run once after paint so item widths are available
    requestAnimationFrame(() => this._checkOverflow())
  }

  disconnect() {
    document.removeEventListener("click",   this._outsideClickHandler)
    document.removeEventListener("keydown", this._escapeHandler)
    window.removeEventListener("resize",    this._resizeHandler)
  }

  // ---------------------------------------------------------------------------
  // Context switcher
  // ---------------------------------------------------------------------------
  toggleContext() {
    const open = !this._hidden(this.contextMenuTarget)
    this._closeAll()
    if (!open) {
      this._show(this.contextMenuTarget)
      this.entityBtnTarget.setAttribute("aria-expanded", "true")
      if (this.hasContextSearchTarget) {
        this.contextSearchTarget.value = ""
        this._resetFilter()
        this.contextSearchTarget.focus()
      }
    }
  }

  filterContext() {
    if (!this.hasContextSearchTarget || !this.hasContextGroupsTarget) return

    const q = this.contextSearchTarget.value.toLowerCase().trim()

    this._qsa(".gv3-context-group", this.contextGroupsTarget).forEach(group => {
      let anyVisible = false
      this._qsa(".gv3-context-item", group).forEach(item => {
        const match = !q || item.textContent.toLowerCase().includes(q)
        item.style.display = match ? "" : "none"
        if (match) anyVisible = true
      })
      group.style.display = anyVisible ? "" : "none"
    })
  }

  // ---------------------------------------------------------------------------
  // Action dropdowns
  // ---------------------------------------------------------------------------
  toggleCreate() {
    this._toggleDropdown(
      this.hasCreateMenuTarget ? this.createMenuTarget : null
    )
  }

  toggleHelp() {
    this._toggleDropdown(
      this.hasHelpMenuTarget ? this.helpMenuTarget : null
    )
  }

  toggleSettings() {
    this._toggleDropdown(
      this.hasSettingsMenuTarget ? this.settingsMenuTarget : null
    )
  }

  // ---------------------------------------------------------------------------
  // Overflow menu
  // ---------------------------------------------------------------------------
  toggleOverflow() {
    this._toggleDropdown(
      this.hasOverflowMenuTarget ? this.overflowMenuTarget : null
    )
  }

  // ---------------------------------------------------------------------------
  // Sidebar
  // ---------------------------------------------------------------------------
  openSidebar() {
    this._sidebar?.classList.add("is-open")
    this._overlay?.classList.add("is-open")
    document.body.classList.add("gv3-sidebar-open")
  }

  closeSidebar() {
    this._sidebar?.classList.remove("is-open")
    this._overlay?.classList.remove("is-open")
    document.body.classList.remove("gv3-sidebar-open")
  }

  // ---------------------------------------------------------------------------
  // Private — binding helpers
  // ---------------------------------------------------------------------------
  _bindNavButtons() {
    // Nav items with sub-dropdowns use click (not hover) for keyboard a11y
    this._qsa(".gv3-menu-item__btn", this.element).forEach(btn => {
      btn.addEventListener("click", e => {
        e.stopPropagation()
        this._toggleNavDropdown(btn)
      })
    })
  }

  _bindContextItems() {
    this._qsa(".gv3-context-item", this.element).forEach(item => {
      item.addEventListener("click", () => this._selectEntity(item))
    })
  }

  _bindOutsideClick() {
    this._outsideClickHandler = e => {
      if (!this.element.contains(e.target)) this._closeAll()
    }
    document.addEventListener("click", this._outsideClickHandler)
  }

  _bindEscape() {
    this._escapeHandler = e => {
      if (e.key === "Escape") {
        this._closeAll()
        this.closeSidebar()
      }
    }
    document.addEventListener("keydown", this._escapeHandler)
  }

  _bindResize() {
    let timer
    this._resizeHandler = () => {
      clearTimeout(timer)
      timer = setTimeout(() => this._checkOverflow(), 120)
    }
    window.addEventListener("resize", this._resizeHandler)
  }

  // ---------------------------------------------------------------------------
  // Private — dropdown state
  // ---------------------------------------------------------------------------
  _toggleDropdown(menuEl) {
    if (!menuEl) return
    const wasOpen = !this._hidden(menuEl)
    this._closeAll()
    if (!wasOpen) this._show(menuEl)
  }

  _toggleNavDropdown(btn) {
    const drop = btn.nextElementSibling
    if (!drop) return
    const wasOpen = drop.classList.contains("is-open")
    // Close all nav dropdowns
    this._qsa(".gv3-nav-dropdown.is-open", this.element).forEach(d => {
      d.classList.remove("is-open")
      d.previousElementSibling?.setAttribute("aria-expanded", "false")
    })
    if (!wasOpen) {
      drop.classList.add("is-open")
      btn.setAttribute("aria-expanded", "true")
    }
  }

  _closeAll() {
    const menus = [
      this.hasContextMenuTarget  && this.contextMenuTarget,
      this.hasCreateMenuTarget   && this.createMenuTarget,
      this.hasHelpMenuTarget     && this.helpMenuTarget,
      this.hasSettingsMenuTarget && this.settingsMenuTarget,
      this.hasOverflowMenuTarget && this.overflowMenuTarget,
    ]
    menus.forEach(m => { if (m) this._hide(m) })

    // Reset aria-expanded on top bar triggers
    this._qsa("[aria-expanded='true']", this.element).forEach(el => {
      // Don't touch nav item buttons — handled separately
      if (!el.classList.contains("gv3-menu-item__btn")) {
        el.setAttribute("aria-expanded", "false")
      }
    })

    // Close nav sub-dropdowns
    this._qsa(".gv3-nav-dropdown.is-open", this.element).forEach(d => {
      d.classList.remove("is-open")
      d.previousElementSibling?.setAttribute("aria-expanded", "false")
    })
  }

  // ---------------------------------------------------------------------------
  // Private — entity selection
  // ---------------------------------------------------------------------------
  _selectEntity(item) {
    const url  = item.dataset.url
    const name = item.dataset.name

    // Update aria-selected
    this._qsa(".gv3-context-item[aria-selected='true']", this.element)
        .forEach(i => i.setAttribute("aria-selected", "false"))
    item.setAttribute("aria-selected", "true")

    // Update button face
    if (this.hasEntityBtnTarget) {
      const nameEl   = this.entityBtnTarget.querySelector(".gv3-header__entity-name")
      const avatarEl = this.entityBtnTarget.querySelector(".gv3-header__entity-avatar")
      const srcImg   = item.querySelector(".gv3-context-item__avatar img")

      if (nameEl && name)  nameEl.textContent = name
      if (avatarEl) {
        if (srcImg) {
          avatarEl.innerHTML = `<img src="${srcImg.src}" alt="${this._esc(name)}">`
        } else {
          avatarEl.textContent = (name || "?").charAt(0).toUpperCase()
        }
      }
    }

    this._closeAll()

    if (url) window.location.href = url
  }

  // ---------------------------------------------------------------------------
  // Private — overflow detection
  // ---------------------------------------------------------------------------
  _checkOverflow() {
    if (!this.hasMainMenuTarget || !this.hasOverflowBtnTarget || !this.hasOverflowMenuTarget) return

    const items     = this._qsa(".gv3-menu-item", this.mainMenuTarget)
    const searchW   = this.hasSearchBoxTarget ? this.searchBoxTarget.offsetWidth : 260
    const backEl    = this.element.querySelector(".gv3-header__back-to-gd")
    const backW     = backEl ? backEl.offsetWidth + 24 : 0
    const container = this.mainMenuTarget.closest(".gv3-header__nav-inner")
    if (!container) return

    // 60 = overflow btn (36) + margins/padding
    const available = container.offsetWidth - searchW - backW - 60

    // Reset — show all items first so we can measure
    items.forEach(i => { i.style.display = "" })
    const overflowUl = this.overflowMenuTarget.querySelector("ul")
    if (overflowUl) overflowUl.innerHTML = ""

    let used       = 0
    let hasOverflow = false

    items.forEach(item => {
      const w = item.offsetWidth
      if (used + w > available && used > 0) {
        hasOverflow = true
        item.style.display = "none"
        if (overflowUl) this._appendToOverflow(overflowUl, item)
      } else {
        used += w
      }
    })

    // Remove trailing divider if present
    if (overflowUl) {
      const last = overflowUl.lastElementChild
      if (last?.classList.contains("gv3-dropdown__divider")) last.remove()
    }

    if (hasOverflow) {
      this.overflowBtnTarget.removeAttribute("hidden")
    } else {
      this.overflowBtnTarget.setAttribute("hidden", "")
      this._hide(this.overflowMenuTarget)
    }
  }

  _appendToOverflow(ul, item) {
    const navDrop = item.querySelector(".gv3-nav-dropdown")

    if (navDrop) {
      // Section label
      const spanEl = item.querySelector(".gv3-menu-item__btn span")
      if (spanEl) {
        const labelLi   = document.createElement("li")
        const labelSpan = document.createElement("span")
        labelSpan.className   = "gv3-dropdown__meta"
        labelSpan.textContent = spanEl.textContent.trim()
        labelLi.appendChild(labelSpan)
        ul.appendChild(labelLi)
      }
      // Sub-links
      navDrop.querySelectorAll("a").forEach(a => {
        const li   = document.createElement("li")
        const link = document.createElement("a")
        link.href      = a.href
        link.className = "gv3-dropdown__item"
        link.innerHTML = a.innerHTML
        li.appendChild(link)
        ul.appendChild(li)
      })
      // Divider after group
      const div = document.createElement("li")
      div.className = "gv3-dropdown__divider"
      ul.appendChild(div)

    } else {
      const a = item.querySelector("a")
      if (a) {
        const li   = document.createElement("li")
        const link = document.createElement("a")
        link.href      = a.href
        link.className = "gv3-dropdown__item"
        link.innerHTML = a.innerHTML
        li.appendChild(link)
        ul.appendChild(li)
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private — DOM utilities
  // ---------------------------------------------------------------------------
  _qsa(selector, ctx = document) {
    return [...ctx.querySelectorAll(selector)]
  }

  _show(el)   { el?.removeAttribute("hidden") }
  _hide(el)   { el?.setAttribute("hidden", "") }
  _hidden(el) { return el ? el.hasAttribute("hidden") : true }

  _resetFilter() {
    if (!this.hasContextGroupsTarget) return
    this._qsa(".gv3-context-group, .gv3-context-item", this.contextGroupsTarget)
        .forEach(el => { el.style.display = "" })
  }

  _esc(str) {
    return (str || "").replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}